import { useState } from 'react';
import { ClipboardList, Loader, CheckCircle2, Columns3, Users } from 'lucide-react';
import type { Task } from '../../types/tasks';
import { useTaskStore } from '../../stores/taskStore';
import EmptyState from '../common/EmptyState';
import TaskCard from './TaskCard';
import TaskAgentView from './TaskAgentView';

const COLUMNS = [
  { key: 'backlog' as const, label: 'Backlog', icon: ClipboardList },
  { key: 'in_progress' as const, label: 'In Progress', icon: Loader },
  { key: 'completed' as const, label: 'Completed', icon: CheckCircle2 },
];

interface TaskKanbanProps {
  onTaskClick: (task: Task) => void;
}

export default function TaskKanban({ onTaskClick }: TaskKanbanProps) {
  const tasks = useTaskStore((s) => s.tasks);
  const updateTask = useTaskStore((s) => s.updateTask);
  const [view, setView] = useState<'kanban' | 'agent'>('kanban');

  function handleStatusChange(id: number, status: string) {
    updateTask(id, { status: status as Task['status'] });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* View Toggle */}
      <div className="flex justify-end">
        <div className="flex bg-bg-tertiary rounded-lg p-0.5">
          <button
            onClick={() => setView('kanban')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'kanban'
                ? 'bg-accent-purple text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            <Columns3 className="h-3.5 w-3.5" />
            Kanban
          </button>
          <button
            onClick={() => setView('agent')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === 'agent'
                ? 'bg-accent-purple text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            Agent
          </button>
        </div>
      </div>

      {view === 'agent' ? (
        <TaskAgentView onTaskClick={onTaskClick} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COLUMNS.map((col) => {
            const columnTasks = tasks.filter((t) => t.status === col.key);
            const Icon = col.icon;

            return (
              <div key={col.key} className="flex flex-col gap-3">
                <div className="flex items-center gap-2 px-1">
                  <Icon className="h-4 w-4 text-text-muted" />
                  <h3 className="text-sm font-medium text-text-primary">{col.label}</h3>
                  <span className="bg-bg-tertiary rounded-full px-2 text-xs text-text-muted">
                    {columnTasks.length}
                  </span>
                </div>

                <div className="flex flex-col gap-2 min-h-[120px]">
                  {columnTasks.length > 0 ? (
                    columnTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onStatusChange={handleStatusChange}
                        onClick={() => onTaskClick(task)}
                      />
                    ))
                  ) : (
                    <EmptyState title="No tasks" description={`No ${col.label.toLowerCase()} tasks`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
